import { useCallback, useState } from 'react'
import { Button, Alert, Spinner, H3 } from '@/common'
import { useTranslation } from 'react-i18next'
import InputPrecursorTable from './InputPrecursorTable'
import { PlusIcon } from '@/common/components/icons'
import { usePermissions } from '@/shared/hooks/usePermissions'
import {
  useEmissionReport,
  useReEvaluateActivityLevelValidationForMethodEmissionReport,
  useUpdateEmissionReport
} from '@/hooks/api/emissionReports'
import { useDialog } from '@/shared/hooks/useDialog'
import EndProductEmittingMethodInputDialog from './dialogs/EndProductEmittingMethodInputDialog'
import { EndProductEmittingMethodInput } from '@/api/EmissionReportModel'
import InputMaterialsTable from './materials/InputMaterialsTable'
import { useDeletePreconsumerScrapMaterial } from '@/shared/hooks/api/preconsumerScrapMaterials'
import { PreconsumerScrapMaterial } from '@/api/PreconsumerScrapMaterialModel'
import PurchasedPrecursorDialog from './dialogs/PurchasedPrecursorDialog'
import AddPreconsumerScrapDialog from './dialogs/PreconsumerScrapDialog'
import {
  PrecursorMaterial,
  usePrecursorMaterialsWithPrecursorEmissionReports
} from '@/hooks/api/v2/precursorMaterials'
import { useSession } from '@/shared/hooks/useSession'

export default function InputsReporting({
  emissionReportId,
  methodEmissionReportId,
  isViewMode,
  installationId,
  isApplicableForActivityLevelValidation,
  reportStartDate,
  reportEndDate
}: {
  emissionReportId: string
  methodEmissionReportId: string
  isViewMode: boolean
  installationId: string
  isApplicableForActivityLevelValidation: boolean
  reportStartDate: string
  reportEndDate: string
}) {
  const { t } = useTranslation()
  const organizationId = useSession().session.organization.id
  const { checkPermission } = usePermissions()
  const canAddPrecursors = checkPermission('precursor', 'create')

  const [selectedEndProductEmittingMethodInput, setSelectedEndProductEmittingMethodInput] =
    useState<EndProductEmittingMethodInput>()
  const [selectedPreconsumerScrapMaterial, setSelectedPreconsumerScrapMaterial] =
    useState<PreconsumerScrapMaterial>()
  const [selectedPrecursorMaterial, setSelectedPrecursorMaterial] = useState<PrecursorMaterial>()

  const { mutateAsync: updateEmissionReport } = useUpdateEmissionReport(emissionReportId)

  const { mutateAsync: reEvaluateActivityLevelValidationForMethodEmissionReport } =
    useReEvaluateActivityLevelValidationForMethodEmissionReport(
      emissionReportId,
      methodEmissionReportId
    )

  const {
    open: isOpenPurchasedPrecursorDialog,
    openDialog: openPurchasedPrecursorDialog,
    closeDialog: closePurchasedPrecursorDialog
  } = useDialog()

  const {
    open: isOpenEndProductEmittingMethodInputDialog,
    openDialog: openEndProductEmittingMethodInputDialog,
    closeDialog: closeEndProductEmittingMethodInputDialog
  } = useDialog()

  const {
    open: isOpenPreconsumerScrapDialog,
    openDialog: openPreconsumerScrapDialog,
    closeDialog: closePreconsumerScrapDialog
  } = useDialog()

  const { data: emissionReport, isPending: isLoadingEmissionReport } =
    useEmissionReport(emissionReportId)

  const { precursorMaterialsWithEmissionReports, isLoading: isLoadingPrecursorMaterials } =
    usePrecursorMaterialsWithPrecursorEmissionReports(organizationId, installationId)

  const precursorMaterialsAllocatedToReport = precursorMaterialsWithEmissionReports.filter(
    precursorMaterial => precursorMaterial.emissionReportId === emissionReportId
  )

  const { mutateAsync: deletePreconsumerScrapMaterial } =
    useDeletePreconsumerScrapMaterial(emissionReportId)

  const preconsumerScrapMaterials = emissionReport ? emissionReport?.preconsumerScrapMaterials : []

  const endProductEmittingMethodInputs = emissionReport?.endProductEmittingMethodInputs ?? []

  const endProductEmittingMethodInputsWithoutDeleted = endProductEmittingMethodInputs.filter(
    endProductEmittingMethodInput => !endProductEmittingMethodInput.deletedAt
  )

  const hasEndProductEmittingMethodInputs = endProductEmittingMethodInputsWithoutDeleted.length > 0
  const hasPrecursorMaterials = precursorMaterialsAllocatedToReport.length > 0
  const hasPreconsumerScrapMaterials = preconsumerScrapMaterials.length > 0

  const onEditPrecursorMaterial = useCallback(
    (precursorMaterialId: string) => {
      setSelectedPrecursorMaterial(
        precursorMaterialsAllocatedToReport.find(
          precursorMaterial => precursorMaterial.id === precursorMaterialId
        )
      )
      openPurchasedPrecursorDialog()
    },
    [precursorMaterialsAllocatedToReport, openPurchasedPrecursorDialog]
  )

  const onRemoveMaterialInput = (id: string | number, isScrap: boolean) => {
    if (isScrap) {
      deletePreconsumerScrapMaterial({ scrapMaterialId: String(id) })
    } else {
      updateEmissionReport({
        endProductEmittingMethodInputs: endProductEmittingMethodInputs.map(
          endProductEmittingMethodInput =>
            endProductEmittingMethodInput.id === id
              ? { ...endProductEmittingMethodInput, deletedAt: new Date() }
              : endProductEmittingMethodInput
        )
      })
    }
    reEvaluateActivityLevelValidationForMethodEmissionReport()
  }

  const onEditMaterialInput = (id: string | number, isScrap: boolean) => {
    if (isScrap) {
      setSelectedPreconsumerScrapMaterial(
        preconsumerScrapMaterials.find(
          preconsumerScrapMaterial => preconsumerScrapMaterial.id === id
        )
      )
      openPreconsumerScrapDialog()
    } else {
      setSelectedEndProductEmittingMethodInput(
        endProductEmittingMethodInputs.find(
          endProductEmittingMethodInput => endProductEmittingMethodInput.id === id
        )
      )
      openEndProductEmittingMethodInputDialog()
    }
  }

  const onCloseEndProductEmittingMethodInputDialog = useCallback(() => {
    setSelectedEndProductEmittingMethodInput(undefined)
    closeEndProductEmittingMethodInputDialog()
  }, [closeEndProductEmittingMethodInputDialog])

  const onClosePreconsumerScrapDialog = useCallback(() => {
    setSelectedPreconsumerScrapMaterial(undefined)
    closePreconsumerScrapDialog()
  }, [closePreconsumerScrapDialog])

  const onClosePurchasedPrecursorDialog = useCallback(() => {
    setSelectedPrecursorMaterial(undefined)
    closePurchasedPrecursorDialog()
  }, [closePurchasedPrecursorDialog])

  const hasMaterials = hasEndProductEmittingMethodInputs || hasPreconsumerScrapMaterials

  const isLoading = isLoadingEmissionReport || isLoadingPrecursorMaterials

  if (isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner className="w-12 h-12" />
      </div>
    )
  }

  return (
    <>
      <div className="space-y-4">
        {isApplicableForActivityLevelValidation && <H3>{t('report:precursors.sectionTitle')}</H3>}
        {hasPrecursorMaterials ? (
          <div className="flex flex-col gap-4">
            <InputPrecursorTable
              emissionReportId={emissionReportId}
              methodEmissionReportId={methodEmissionReportId}
              isViewMode={isViewMode}
              precursorMaterials={precursorMaterialsAllocatedToReport}
              onEdit={onEditPrecursorMaterial}
              installationId={installationId}
            />
          </div>
        ) : (
          <div className="space-y-4">
            <Alert type="info">{t('report:precursors.noItems')}</Alert>
          </div>
        )}
        {isApplicableForActivityLevelValidation && (
          <h3 className="font-semibold">{t('report:materials.title')}</h3>
        )}
        {isApplicableForActivityLevelValidation && !hasMaterials && (
          <div className="space-y-4">
            <Alert type="info">
              {t('report:materials.endProductEmittingMethodInputs.noItems')}
            </Alert>
          </div>
        )}
        {hasMaterials && (
          <div className="flex flex-col gap-4">
            <InputMaterialsTable
              preconsumerScrapMaterials={preconsumerScrapMaterials}
              endProductEmittingMethodInputs={endProductEmittingMethodInputsWithoutDeleted}
              onRemove={onRemoveMaterialInput}
              onEdit={onEditMaterialInput}
              isViewMode={isViewMode}
            />
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button
            variant="subtle"
            sizeType="sm"
            onClick={openPurchasedPrecursorDialog}
            disabled={isViewMode || !canAddPrecursors}
            icon={<PlusIcon />}
          >
            {t('report:inputs.actions.addPurchasedPrecursor')}
          </Button>
          {isApplicableForActivityLevelValidation && (
            <>
              <Button
                variant="subtle"
                sizeType="sm"
                onClick={openPreconsumerScrapDialog}
                disabled={isViewMode || !canAddPrecursors}
                icon={<PlusIcon />}
              >
                {t('report:inputs.actions.addPreConsumerScrap')}
              </Button>
              <Button
                variant="subtle"
                sizeType="sm"
                onClick={openEndProductEmittingMethodInputDialog}
                disabled={isViewMode || !canAddPrecursors}
                icon={<PlusIcon />}
              >
                {t('report:inputs.actions.addOtherMaterial')}
              </Button>
            </>
          )}
        </div>
      </div>
      <PurchasedPrecursorDialog
        emissionReportId={emissionReportId}
        methodEmissionReportId={methodEmissionReportId}
        installationId={installationId}
        isOpen={isOpenPurchasedPrecursorDialog}
        close={onClosePurchasedPrecursorDialog}
        reportStartDate={reportStartDate}
        reportEndDate={reportEndDate}
        initialState={selectedPrecursorMaterial}
      />
      <EndProductEmittingMethodInputDialog
        emissionReportId={emissionReportId}
        methodEmissionReportId={methodEmissionReportId}
        isOpen={isOpenEndProductEmittingMethodInputDialog}
        close={onCloseEndProductEmittingMethodInputDialog}
        initialState={selectedEndProductEmittingMethodInput}
        endProductEmittingMethodInputs={endProductEmittingMethodInputsWithoutDeleted}
      />
      <AddPreconsumerScrapDialog
        emissionReportId={emissionReportId}
        methodEmissionReportId={methodEmissionReportId}
        initialState={selectedPreconsumerScrapMaterial}
        isOpen={isOpenPreconsumerScrapDialog}
        close={onClosePreconsumerScrapDialog}
        reportStartDate={reportStartDate}
        installationId={installationId}
      />
    </>
  )
}
